/* eslint-disable no-console */
// console statements are wrapped in options; idb is not visible normally
import { Action } from '@ngrx/store';
import { setMany } from 'idb-keyval';
import { take } from 'rxjs/operators';
import { NgrxStoreIdbStoreProvider } from './ngrx-store-idb-store.provider';
import {
  KeyConfiguration,
  Keys,
  NgrxStoreIdbOptions,
  SAVED_STATE_KEY,
  SAVED_STATE_PREFIX,
  savedEntryRecordKey,
  savedRecordKey,
} from './ngrx-store-idb.options';
import { NgrxStoreIdbService } from './ngrx-store-idb.service';

// Recursively traverse all properties of the existing slice as defined by the `filter` argument,
// and output the new object with extraneous properties removed.
export const createStateSlice = (existingSlice: any, keys: (string | number | KeyConfiguration)[]) => {
  return keys.reduce(
    (memo: { [x: string]: any; [x: number]: any }, attr: string | number | KeyConfiguration) => {
      if (typeof attr === 'string' || typeof attr === 'number') {
        const value = existingSlice?.[attr];
        if (value != null) {
          memo[attr] = value;
        }
      } else {
        for (const key in attr) {
          if (Object.prototype.hasOwnProperty.call(attr, key)) {
            const element = attr[key];
            memo[key] = createStateSlice(existingSlice[key], element);
          }
        }
      }
      return memo;
    },
    {},
  );
};

const keyName = (key: string | KeyConfiguration): string =>
  typeof key === 'object' ? Object.keys(key)[0] : key;

/**
 * Marshals a state object into the per-slice record layout. Used for the
 * one-time migration of a legacy single-record state.
 */
export const stateToRecords = (
  state: any,
  keys: Keys,
  perEntryKeys: string[],
): [string, unknown][] => {
  const records: [string, unknown][] = [];
  for (const key of keys) {
    const name = keyName(key);
    const slice = state?.[name];
    if (slice == null) {
      continue;
    }
    if (typeof key === 'object') {
      records.push([savedRecordKey(name), createStateSlice(slice, key[name])]);
    } else if (perEntryKeys.includes(name)) {
      for (const entry of Object.keys(slice)) {
        records.push([savedEntryRecordKey(name, entry), slice[entry]]);
      }
    } else {
      records.push([savedRecordKey(name), slice]);
    }
  }
  return records;
};

/**
 * Reassembles a state object from per-slice records, the inverse of
 * `stateToRecords`. Non-`State/` records and nullish values (tombstones of
 * removed slices) are skipped.
 */
export const recordsToState = (records: [IDBValidKey, unknown][]): any => {
  const state = {};
  for (const [recordKey, value] of records) {
    if (
      typeof recordKey !== 'string' ||
      !recordKey.startsWith(SAVED_STATE_PREFIX) ||
      value == null
    ) {
      continue;
    }
    const path = recordKey.slice(SAVED_STATE_PREFIX.length);
    const slash = path.indexOf('/');
    if (slash < 0) {
      state[path] = value;
    } else {
      const name = path.slice(0, slash);
      state[name] ??= {};
      state[name][path.slice(slash + 1)] = value;
    }
  }
  return state;
};

/**
 * Collects changed state slices per action (reference equality - reducers are
 * immutable) and writes them to IndexedDB as per-slice records, coalesced off
 * the dispatch call stack.
 *
 * Invariants:
 * - Change detection compares against the latest *known* value of a record:
 *   the pending dirty value if one is queued, else the last successfully
 *   written one. The dirty map always holds the newest unwritten value.
 * - Flushes are single-flight. After a write settles, a record is cleared
 *   only if its dirty value is still the one that was written; a value queued
 *   during the write survives and reschedules. Rejected writes retain all
 *   records and retry on the next flush.
 * - A trailing debounce coalesces action bursts; the delay is clamped so the
 *   first unwritten change never waits longer than `flush.maxWaitMs`.
 * - `pagehide`/`visibilitychange:hidden` flush immediately (best effort), so
 *   at most one debounce window of state is lost on tab close.
 */
export class NgrxStoreIdbFlushQueue {
  /** record key -> newest value not yet successfully written */
  private dirty = new Map<string, unknown>();

  /** record key -> last successfully written value */
  private lastSaved = new Map<string, unknown>();

  /** top-level key name -> slice reference last examined (skip fast path) */
  private topRefs = new Map<string, unknown>();

  /** per-entry key name -> entry names present at last examination */
  private knownEntries = new Map<string, Set<string>>();

  private timer: ReturnType<typeof setTimeout> | null = null;

  private firstDirtyAt: number | null = null;

  private inflight = false;

  private lastAction: Action | null = null;

  constructor(
    private opts: NgrxStoreIdbOptions,
    private storeProvider: NgrxStoreIdbStoreProvider,
    private service: NgrxStoreIdbService,
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flushNow());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushNow();
        }
      });
    }
    // Records dirtied before the (lazily created) tenant store exists are
    // retained without a timer; flush them once the store arrives, even if
    // no further action touches a persisted slice.
    this.storeProvider.store$.pipe(take(1)).subscribe(() => {
      if (this.dirty.size > 0) {
        this.schedule();
      }
    });
  }

  /**
   * Records the given state as already persisted (used after rehydration) so
   * only subsequent changes are written. `keys` may be a subset when a lazy
   * feature slice rehydrates via UPDATE.
   *
   * Pending dirty values for the seeded records are discarded: they predate
   * the rehydration merge (which already folded them into `state`), and a
   * later flush of them would overwrite the rehydrated records on disk.
   */
  seed(state: any, keys: Keys | null): void {
    if (!keys) {
      this.topRefs.set(SAVED_STATE_KEY, state);
      this.dirty.delete(SAVED_STATE_KEY);
      return;
    }
    for (const key of keys) {
      const name = keyName(key);
      const slice = state?.[name];
      this.topRefs.set(name, slice);
      if (typeof key === 'object') {
        const recordKey = savedRecordKey(name);
        this.lastSaved.set(recordKey, slice == null ? undefined : createStateSlice(slice, key[name]));
        this.dirty.delete(recordKey);
      } else if (this.opts.perEntryKeys.includes(name)) {
        const prefix = `${SAVED_STATE_PREFIX}${name}/`;
        for (const recordKey of [...this.dirty.keys()]) {
          if (recordKey.startsWith(prefix)) {
            this.dirty.delete(recordKey);
          }
        }
        const entries = slice == null ? [] : Object.keys(slice);
        for (const entry of entries) {
          this.lastSaved.set(savedEntryRecordKey(name, entry), slice[entry]);
        }
        this.knownEntries.set(name, new Set(entries));
      } else {
        const recordKey = savedRecordKey(name);
        this.lastSaved.set(recordKey, slice);
        this.dirty.delete(recordKey);
      }
    }
  }

  /** Marks the slices changed by this action dirty and schedules a flush. */
  update(state: any, action: Action): void {
    let marked = false;
    if (this.opts.keys) {
      marked = this.collect(state, this.opts.keys, (recordKey, value) => {
        this.dirty.set(recordKey, value);
      });
    } else if (this.topRefs.get(SAVED_STATE_KEY) !== state) {
      this.topRefs.set(SAVED_STATE_KEY, state);
      this.dirty.set(SAVED_STATE_KEY, this.opts.marshaller(state));
      marked = true;
    }
    if (marked) {
      this.lastAction = action;
      this.firstDirtyAt ??= Date.now();
      this.schedule();
    }
  }

  /** Immediate best-effort flush, bypassing the debounce and single-flight. */
  flushNow(): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    // IndexedDB orders transactions by creation, so writing even while an
    // earlier flush is in flight is safe: the newer write commits last.
    this.performFlush();
  }

  /**
   * Walks `keys` and emits (recordKey, newest value) for every record whose
   * value differs by reference from the latest known one. Returns whether
   * anything was emitted.
   */
  private collect(
    state: any,
    keys: Keys,
    sink: (recordKey: string, value: unknown) => void,
  ): boolean {
    let emitted = false;
    const emit = (recordKey: string, value: unknown) => {
      if (value !== this.latestKnown(recordKey)) {
        sink(recordKey, value);
        emitted = true;
      }
    };
    for (const key of keys) {
      const name = keyName(key);
      const slice = state?.[name];
      if (typeof key === 'object') {
        // Nested key configurations marshal a filtered copy, so only rebuild
        // (and possibly write) when the top-level slice reference changed.
        if (this.topRefs.get(name) !== slice) {
          this.topRefs.set(name, slice);
          if (slice != null) {
            sink(savedRecordKey(name), createStateSlice(slice, key[name]));
            emitted = true;
          } else {
            // Tombstone a removed slice or its record would be rehydrated.
            emit(savedRecordKey(name), undefined);
          }
        }
      } else if (this.opts.perEntryKeys.includes(name)) {
        if (this.topRefs.get(name) === slice) {
          continue;
        }
        this.topRefs.set(name, slice);
        if (slice == null) {
          for (const entry of this.knownEntries.get(name) ?? []) {
            emit(savedEntryRecordKey(name, entry), undefined);
          }
          this.knownEntries.set(name, new Set());
          continue;
        }
        const entries = Object.keys(slice);
        for (const entry of entries) {
          emit(savedEntryRecordKey(name, entry), slice[entry]);
        }
        // Entries removed from the slice leave a nullish tombstone that the
        // rehydration read skips.
        const previous = this.knownEntries.get(name);
        if (previous) {
          for (const entry of previous) {
            if (!(entry in slice)) {
              emit(savedEntryRecordKey(name, entry), undefined);
            }
          }
        }
        this.knownEntries.set(name, new Set(entries));
      } else {
        emit(savedRecordKey(name), slice);
      }
    }
    return emitted;
  }

  private latestKnown(recordKey: string): unknown {
    return this.dirty.has(recordKey) ? this.dirty.get(recordKey) : this.lastSaved.get(recordKey);
  }

  private schedule(): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
    }
    const { debounceMs, maxWaitMs } = this.opts.flush;
    const untilMaxWait = (this.firstDirtyAt ?? Date.now()) + maxWaitMs - Date.now();
    const delay = Math.max(0, Math.min(debounceMs, untilMaxWait));
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.inflight) {
        // The settling flush reschedules anything still dirty.
        return;
      }
      this.performFlush();
    }, delay);
  }

  private performFlush(): void {
    if (this.dirty.size === 0) {
      return;
    }
    const idbStore = this.storeProvider.getIfReady();
    if (!idbStore) {
      if (this.opts.debugInfo) {
        console.debug('NgrxStoreIdb: State will not be persisted. Tenant store not initialised yet.');
      }
      return;
    }
    if (!this.service.canConcurrentlySync()) {
      if (this.opts.debugInfo) {
        console.debug(
          'NgrxStoreIdb: State will not be persisted. Application runs also in other tab/window.',
        );
      }
      return;
    }

    const snapshot = [...this.dirty.entries()];
    const action = this.lastAction;
    this.inflight = true;
    setMany(snapshot, idbStore)
      .then(() => {
        for (const [recordKey, value] of snapshot) {
          this.lastSaved.set(recordKey, value);
          if (this.dirty.get(recordKey) === value) {
            this.dirty.delete(recordKey);
          }
        }
        this.firstDirtyAt = this.dirty.size > 0 ? Date.now() : null;
        this.service.broadcastSyncEvent(action);
        if (this.opts.debugInfo) {
          console.debug('NgrxStoreIdb: Store state persisted to IndexedDB', snapshot, action);
        }
      })
      .catch((err) => {
        console.error('NgrxStoreIdb: Error storing state to IndexedDB', err, action);
        // Restart the max-wait window, or a persistent failure past maxWaitMs
        // would make schedule() compute a zero delay and retry in a tight
        // loop. This paces retries at the debounce cadence instead.
        this.firstDirtyAt = Date.now();
        this.service.broadcastSyncEvent(action);
      })
      .finally(() => {
        this.inflight = false;
        if (this.dirty.size > 0) {
          this.schedule();
        }
      });
  }
}
